import { useFormik } from 'formik'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import * as Yup from 'yup'
import {
  ButtonType,
  OptionsHelper,
  formatDateToPtBr,
  showAlert,
  showConfirmAlert,
} from '../../../../../_metronic/helpers'
import {
  Button,
  FormDateInput,
  FormSelect,
  FormTimeInput,
  IOption,
  InfoAlert,
  LoadingIndicator,
  useModal,
} from '../../../../components'
import { EspecialidadeList } from '../../especialidades/especialidades-list/core/_models'
import { isVinculoMedicoAtivo } from 'app/modules/auth/core/vinculos'
import { FuncionarioView } from '../../funcionarios/funcionarios-list/core/_models'
import {
  DiaSemana,
  EscalaDtoCadastroEmpresa,
  EscalaDtoCadastroPessoa,
  EscalaViewEmpresa,
  EscalaViewPessoa,
  TipoEscala,
  TipoAtendimentoEscala,
} from '../core/_models'
import {
  createEscalaEmpresa,
  createEscalaPessoa,
  deleteEscalaEmpresa,
  deleteEscalaPessoa,
  getMeusVinculos,
  getVinculosPorEmpresaEPessoa,
} from '../core/_requests'
import {
  DIAS_SEMANA_OPTIONS,
  ErrosValidacao,
  MENSAGEM_ESCALA_DUPLICADA,
  TIPO_ESCALA_LABELS,
  ehErroDeEscalaDuplicada,
  extrairErrosValidacao,
  formatHorario,
  mensagensDeFalha,
} from '../core/escalaHelpers'
import { ErrosVinculos } from './ErrosVinculos'

export type EixoEscala = 'pessoal' | 'empresa'

type Props = {
  eixo: EixoEscala
  /** Obrigatório no eixo empresa — é o `{empresa}` do path. */
  empresa?: number
  /**
   * Escala já existente usada para pré-preencher o formulário. Quando presente,
   * salvar executa **excluir + recriar**: o backend não tem `PUT /escalas/{id}`,
   * só `PUT /escalas/agenda` (um dia). Ver `EscalaAgendaDiaModal` para a edição
   * de um dia.
   */
  escalaOriginal?: EscalaViewPessoa | EscalaViewEmpresa
  onSuccess: () => void
}

type ValoresFormulario = {
  tipo: IOption | null
  pessoa: IOption | null
  empresas: IOption[]
  especialidades: IOption[]
  tiposAtendimento: IOption[]
  diasSemana: IOption[]
  dataInicio: string
  dataTermino: string
  horarioInicio: string
  horarioTermino: string
}

const TIPO_OPTIONS: IOption[] = [
  { value: TipoEscala.Disponibilidade, label: TIPO_ESCALA_LABELS[TipoEscala.Disponibilidade] },
  { value: TipoEscala.Indisponibilidade, label: TIPO_ESCALA_LABELS[TipoEscala.Indisponibilidade] },
]

const ehDisponibilidade = (tipo: IOption | null) => tipo?.value === TipoEscala.Disponibilidade

/**
 * Espelha o `@ConditionalRequired(field = "tipo", value = "DISPONIBILIDADE", ...)`
 * dos DTOs do backend: com `DISPONIBILIDADE`, empresa/especialidade/tipo de
 * atendimento/horários são obrigatórios; com `INDISPONIBILIDADE`, empresa é
 * opcional e deixá-la em branco vale para todas as empresas em que a pessoa tem
 * vínculo de médico ativo.
 */
const montaSchema = (eixo: EixoEscala) =>
  Yup.object({
    tipo: Yup.object().nullable().required('Informe o tipo da marcação'),
    pessoa:
      eixo === 'empresa'
        ? Yup.object().nullable().required('Informe o profissional')
        : Yup.object().nullable(),
    dataInicio: Yup.string().required('Informe o início da vigência'),
    dataTermino: Yup.string().test(
      'termino-depois-do-inicio',
      'O fim da vigência não pode ser anterior ao início',
      function (valor) {
        const inicio = this.parent.dataInicio
        return !valor || !inicio || valor >= inicio
      }
    ),
    empresas: Yup.array().when('tipo', {
      is: ehDisponibilidade,
      then: (schema) =>
        eixo === 'pessoal'
          ? schema.min(1, 'Escolha ao menos uma instituição para a disponibilidade')
          : schema,
      otherwise: (schema) => schema,
    }),
    especialidades: Yup.array().when('tipo', {
      is: ehDisponibilidade,
      then: (schema) => schema.min(1, 'Escolha ao menos uma especialidade'),
      otherwise: (schema) => schema,
    }),
    tiposAtendimento: Yup.array().when('tipo', {
      is: ehDisponibilidade,
      then: (schema) => schema.min(1, 'Escolha ao menos um tipo de atendimento'),
      otherwise: (schema) => schema,
    }),
    horarioInicio: Yup.string().when('tipo', {
      is: ehDisponibilidade,
      then: (schema) => schema.required('Informe o horário de início'),
      otherwise: (schema) => schema,
    }),
    horarioTermino: Yup.string()
      .when('tipo', {
        is: ehDisponibilidade,
        then: (schema) => schema.required('Informe o horário de término'),
        otherwise: (schema) => schema,
      })
      .test(
        'termino-depois-do-inicio',
        'O horário de término deve ser posterior ao de início',
        function (valor) {
          const inicio = this.parent.horarioInicio
          return !valor || !inicio || valor > inicio
        }
      ),
  })

const paraOptions = (
  itens: Array<{ id?: number | null; nome?: string | null }> | undefined
): IOption[] =>
  (itens ?? [])
    .filter((item) => item?.id != null)
    .map((item) => ({ value: item.id as number, label: item.nome ?? String(item.id) }))

const semRepetirPorValor = (options: IOption[]): IOption[] => {
  const vistos = new Set<IOption['value']>()
  return options.filter((option) => {
    if (vistos.has(option.value)) return false
    vistos.add(option.value)
    return true
  })
}

const rotulosDe = (itens: IOption[]): string =>
  itens.length ? itens.map((item) => item.label).join(', ') : '—'

/**
 * Resumo do que está preenchido, em uma linha, para o usuário conseguir
 * **recriar a escala à mão** se sair da tela na janela entre o `DELETE` e o
 * `POST` da substituição. Sem `PUT /escalas/{id}` no backend, essa janela
 * existe; o que a tela deve a ela é não deixar o dado sumir sem registro.
 */
const resumoParaRecriar = (valores: ValoresFormulario): string =>
  [
    `Tipo: ${valores.tipo?.label ?? '—'}`,
    ...(valores.pessoa ? [`Profissional: ${valores.pessoa.label}`] : []),
    `Instituições: ${rotulosDe(valores.empresas)}`,
    `Especialidades: ${rotulosDe(valores.especialidades)}`,
    `Tipos de atendimento: ${rotulosDe(valores.tiposAtendimento)}`,
    `Dias: ${
      valores.diasSemana.length ? rotulosDe(valores.diasSemana) : 'todos os dias do período'
    }`,
    `Vigência: ${formatDateToPtBr(valores.dataInicio) || '—'}${
      valores.dataTermino ? ` a ${formatDateToPtBr(valores.dataTermino)}` : ''
    }`,
    `Horário: ${
      valores.horarioInicio || valores.horarioTermino
        ? `${valores.horarioInicio || '—'} às ${valores.horarioTermino || '—'}`
        : 'dia inteiro'
    }`,
  ].join(' · ')

export const EscalaFormModal: FC<Props> = ({ eixo, empresa, escalaOriginal, onSuccess }) => {
  const { closeModal, registrarGuardaFechamento } = useModal()

  const [vinculos, setVinculos] = useState<FuncionarioView[]>([])
  const [carregandoVinculos, setCarregandoVinculos] = useState(eixo === 'pessoal')
  const [especialidadesDoProfissional, setEspecialidadesDoProfissional] = useState<
    EspecialidadeList[]
  >([])
  const [erros, setErros] = useState<ErrosValidacao | null>(null)
  const [escalaAnteriorRemovida, setEscalaAnteriorRemovida] = useState(false)

  const escalaOriginalEmpresa = escalaOriginal as EscalaViewEmpresa | undefined

  /* Eixo pessoal: empresas e especialidades saem do próprio vínculo (GET /funcionarios/me),
     que é a mesma fonte que o backend consulta em processaVinculos(). */
  useEffect(() => {
    if (eixo !== 'pessoal') return

    let ativo = true
    setCarregandoVinculos(true)
    getMeusVinculos()
      .then((dados) => {
        if (ativo) setVinculos(dados ?? [])
      })
      .catch((error) => setErros(extrairErrosValidacao(error)))
      .finally(() => {
        if (ativo) setCarregandoVinculos(false)
      })

    return () => {
      ativo = false
    }
  }, [eixo])

  const vinculosMedicosAtivos = useMemo(() => vinculos.filter(isVinculoMedicoAtivo), [vinculos])

  const opcoesEmpresas = useMemo(
    () =>
      semRepetirPorValor(
        vinculosMedicosAtivos
          .filter((vinculo) => vinculo.empresa?.id != null)
          .map((vinculo) => ({
            value: vinculo.empresa!.id,
            label: vinculo.empresa!.nomeFantasia ?? vinculo.empresa!.nome ?? '',
          }))
      ),
    [vinculosMedicosAtivos]
  )

  const formik = useFormik<ValoresFormulario>({
    initialValues: {
      tipo: TIPO_OPTIONS.find((opcao) => opcao.value === escalaOriginal?.tipo) ?? TIPO_OPTIONS[0],
      pessoa: escalaOriginalEmpresa?.pessoa
        ? {
            value: escalaOriginalEmpresa.pessoa.id,
            label: escalaOriginalEmpresa.pessoa.nome,
          }
        : null,
      empresas: paraOptions(
        (escalaOriginal as EscalaViewPessoa | undefined)?.empresas?.map((item) => ({
          id: item.id,
          nome: item.nomeFantasia ?? item.nome,
        }))
      ),
      especialidades: semRepetirPorValor(
        paraOptions(
          escalaOriginal?.vinculos
            ?.map((vinculo) => vinculo.especialidade)
            .filter((especialidade): especialidade is EspecialidadeList => !!especialidade)
        )
      ),
      tiposAtendimento: (escalaOriginal?.tiposAtendimento ?? []).map((tipo) => ({
        value: tipo,
        label: tipo,
      })),
      diasSemana: DIAS_SEMANA_OPTIONS.filter((opcao) =>
        ((escalaOriginal as EscalaViewPessoa | undefined)?.diasSemana ?? []).includes(
          opcao.value as DiaSemana
        )
      ),
      dataInicio: escalaOriginal?.dataInicio ?? '',
      dataTermino: escalaOriginal?.dataTermino ?? '',
      horarioInicio: formatHorario(escalaOriginal?.horarioInicio),
      horarioTermino: formatHorario(escalaOriginal?.horarioTermino),
    },
    validationSchema: montaSchema(eixo),
    onSubmit: async (values, { setSubmitting }) => {
      setErros(null)

      const disponibilidade = ehDisponibilidade(values.tipo)
      const corpoComum = {
        tipo: values.tipo!.value as TipoEscala,
        dataInicio: values.dataInicio,
        dataTermino: values.dataTermino || null,
        horarioInicio: values.horarioInicio || null,
        horarioTermino: values.horarioTermino || null,
        diasSemana: values.diasSemana.length
          ? (values.diasSemana.map((opcao) => opcao.value) as DiaSemana[])
          : null,
        especialidades: values.especialidades.length
          ? (values.especialidades.map((opcao) => Number(opcao.value)) as number[])
          : null,
        tiposAtendimento: values.tiposAtendimento.length
          ? (values.tiposAtendimento.map((opcao) => opcao.value) as TipoAtendimentoEscala[])
          : null,
      }

      if (escalaOriginal && !escalaAnteriorRemovida) {
        const confirmado = await showConfirmAlert({
          title: 'Substituir a escala?',
          text:
            'A escala atual será removida e recriada com os novos dados. ' +
            'Marcações já geradas para os dias dessa escala serão apagadas.',
          icon: 'warning',
          confirmButtonText: 'Sim, substituir',
        })
        if (!confirmado) {
          setSubmitting(false)
          return
        }
      }

      setSubmitting(true)
      try {
        if (escalaOriginal && !escalaAnteriorRemovida) {
          // Excluir antes de recriar: `escalas_agenda_uk` é único em
          // (pessoa, tipo, data, horário), então criar primeiro colidiria com a
          // própria escala que está sendo substituída.
          if (eixo === 'empresa') await deleteEscalaEmpresa(empresa!, escalaOriginal.id)
          else await deleteEscalaPessoa(escalaOriginal.id)
          setEscalaAnteriorRemovida(true)
        }

        if (eixo === 'empresa') {
          const dto: EscalaDtoCadastroEmpresa = {
            ...corpoComum,
            pessoa: Number(values.pessoa!.value),
          }
          await createEscalaEmpresa(empresa!, dto)
        } else {
          const dto: EscalaDtoCadastroPessoa = {
            ...corpoComum,
            empresas: values.empresas.length
              ? values.empresas.map((opcao) => Number(opcao.value))
              : null,
          }
          await createEscalaPessoa(dto)
        }

        showAlert({
          title: 'Pronto!',
          text: escalaOriginal
            ? 'Escala substituída com sucesso.'
            : disponibilidade
            ? 'Disponibilidade registrada com sucesso.'
            : 'Indisponibilidade registrada com sucesso.',
          icon: 'success',
        })
        onSuccess()
        closeModal()
      } catch (error) {
        // Nada de catch silencioso nem toast genérico: o 422 de `processaVinculos`
        // traz, em `errors.vinculos`, um item por empresa/especialidade recusada —
        // é exatamente o que o usuário precisa ver para corrigir a escala.
        setErros(extrairErrosValidacao(error))
      } finally {
        setSubmitting(false)
      }
    },
  })

  /* Eixo empresa: as especialidades oferecidas são as do vínculo daquele
     profissional naquela empresa — a mesma lista que o backend aceita. */
  useEffect(() => {
    if (eixo !== 'empresa' || !empresa || !formik.values.pessoa) {
      setEspecialidadesDoProfissional([])
      return
    }

    let ativo = true
    getVinculosPorEmpresaEPessoa(empresa, Number(formik.values.pessoa.value))
      .then((dados) => {
        if (!ativo) return
        setEspecialidadesDoProfissional(
          (dados ?? [])
            .filter(isVinculoMedicoAtivo)
            .flatMap((vinculo) => vinculo.especialidades ?? [])
        )
      })
      .catch((error) => setErros(extrairErrosValidacao(error)))

    return () => {
      ativo = false
    }
  }, [eixo, empresa, formik.values.pessoa])

  const opcoesEspecialidades = useMemo(() => {
    if (eixo === 'empresa') return semRepetirPorValor(paraOptions(especialidadesDoProfissional))

    const empresasEscolhidas = new Set(formik.values.empresas.map((opcao) => opcao.value))
    const fonte = empresasEscolhidas.size
      ? vinculosMedicosAtivos.filter((vinculo) => empresasEscolhidas.has(vinculo.empresa?.id ?? -1))
      : vinculosMedicosAtivos

    return semRepetirPorValor(paraOptions(fonte.flatMap((vinculo) => vinculo.especialidades ?? [])))
  }, [eixo, especialidadesDoProfissional, formik.values.empresas, vinculosMedicosAtivos])

  const disponibilidade = ehDisponibilidade(formik.values.tipo)
  const indisponibilidadeGlobal =
    eixo === 'pessoal' && !disponibilidade && formik.values.empresas.length === 0

  /*
   * Indisponibilidade sem empresa é **necessariamente total**: `processaVinculos`
   * itera sobre as empresas do request, então com a lista vazia nenhum
   * `EscalaEmpresa` nasce e a especialidade escolhida nunca é lida — o bloqueio
   * sai valendo para tudo, sem aviso. A tela impede o caso em vez de deixar o
   * usuário pedir "bloqueie só Cardiologia" e receber bloqueio de tudo.
   * (Na branch de correção do backend esse mesmo request passa a devolver 422
   * com o campo `especialidades`; aqui ele nem chega a ser montado.)
   */
  const { setFieldValue } = formik
  const especialidadesEscolhidas = formik.values.especialidades.length
  useEffect(() => {
    if (indisponibilidadeGlobal && especialidadesEscolhidas) setFieldValue('especialidades', [])
  }, [indisponibilidadeGlobal, especialidadesEscolhidas, setFieldValue])

  /*
   * Janela entre o DELETE e o POST da substituição: enquanto a escala anterior
   * já foi removida e a nova não nasceu, fechar o modal apaga a escala para
   * sempre (não existe `PUT /escalas/{id}`). Em vez de `disableClose` — que
   * tiraria o Esc e o X e viraria armadilha de teclado —, o fechamento passa a
   * pedir confirmação e a mostrar o que anotar para recriar à mão.
   */
  const fecharComConfirmacao = useCallback(async () => {
    if (!escalaAnteriorRemovida) {
      closeModal()
      return
    }

    const confirmado = await showConfirmAlert({
      title: 'Sair sem recriar a escala?',
      text:
        'A escala anterior já foi excluída e a nova ainda não foi criada — sair agora deixa o ' +
        'profissional sem essa escala, e não há como desfazer. Anote os dados para recriar: ' +
        resumoParaRecriar(formik.values),
      icon: 'warning',
      confirmButtonText: 'Sim, sair mesmo assim',
    })
    if (confirmado) closeModal()
  }, [closeModal, escalaAnteriorRemovida, formik.values])

  useEffect(() => {
    if (!escalaAnteriorRemovida) return

    // A guarda devolve `false` (não feche agora) e assume a confirmação; o
    // `closeModal` sai de lá quando o usuário confirma.
    registrarGuardaFechamento(() => {
      void fecharComConfirmacao()
      return false
    })
    return () => registrarGuardaFechamento(undefined)
  }, [escalaAnteriorRemovida, fecharComConfirmacao, registrarGuardaFechamento])

  /*
   * A guarda acima cobre X, Esc e backdrop, mas não fechar a aba nem recarregar
   * a página — que perdem a escala do mesmo jeito. O navegador só permite o
   * diálogo nativo dele (texto próprio, sem o resumo para recriar).
   */
  useEffect(() => {
    if (!escalaAnteriorRemovida) return

    const avisarAntesDeSair = (evento: BeforeUnloadEvent) => {
      evento.preventDefault()
      // Chrome/Edge ainda exigem `returnValue` para exibir o diálogo.
      evento.returnValue = ''
    }
    window.addEventListener('beforeunload', avisarAntesDeSair)
    return () => window.removeEventListener('beforeunload', avisarAntesDeSair)
  }, [escalaAnteriorRemovida])

  const errosDeVinculo = erros?.porCampo.vinculos ?? []
  const exibiuDuplicada = !!erros && ehErroDeEscalaDuplicada(erros)
  // Sempre sobra uma mensagem quando houve erro: sem envelope (rede, timeout,
  // CORS) o usuário clicaria em salvar e não veria nada mudar.
  const outrosErros = mensagensDeFalha(erros, {
    exibiuVinculos: errosDeVinculo.length > 0,
    exibiuDuplicada,
  })

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      {escalaAnteriorRemovida && (
        <InfoAlert variant='warning' title='A escala anterior já foi removida'>
          A exclusão foi concluída, mas a nova escala ainda não foi criada. Corrija os pontos abaixo
          e salve novamente — sair agora deixaria o profissional sem essa escala, e fechar a janela
          passa a pedir confirmação. Dados para recriar à mão, se precisar:{' '}
          <strong>{resumoParaRecriar(formik.values)}</strong>
        </InfoAlert>
      )}

      {exibiuDuplicada && (
        <InfoAlert variant='warning' title='Marcação já cadastrada'>
          {MENSAGEM_ESCALA_DUPLICADA}
        </InfoAlert>
      )}

      <ErrosVinculos
        id='escala-erros-vinculos'
        titulo='Estes vínculos não foram aceitos e a escala não foi salva:'
        itens={errosDeVinculo}
      />
      <ErrosVinculos titulo='Não foi possível salvar:' itens={outrosErros} />

      <div className='row'>
        <FormSelect
          formik={formik}
          name='tipo'
          label='Tipo de marcação'
          options={TIPO_OPTIONS}
          className='col-md-6 mb-5'
          isRequired
        />

        {eixo === 'empresa' && (
          <FormSelect
            formik={formik}
            name='pessoa'
            label='Profissional'
            placeholder='Busque pelo nome'
            fetchOptions={OptionsHelper.getOptionsProfissionalSaude}
            className='col-md-6 mb-5'
            isRequired
            isClearable
          />
        )}

        {eixo === 'pessoal' && (
          <FormSelect
            formik={formik}
            name='empresas'
            label='Instituições'
            placeholder={
              carregandoVinculos ? 'Carregando seus vínculos...' : 'Selecione as instituições'
            }
            options={opcoesEmpresas}
            isMulti
            className='col-md-6 mb-5'
            isRequired={disponibilidade}
            isDisabled={carregandoVinculos}
            infoText='Só aparecem as instituições em que você tem vínculo de médico ativo.'
          />
        )}
      </div>

      {indisponibilidadeGlobal && (
        <InfoAlert variant='info' title='Indisponibilidade para todas as suas instituições'>
          Sem empresa selecionada, esta indisponibilidade vale para todas as empresas em que você
          tem vínculo de médico ativo — inclusive as que vierem depois. O bloqueio é necessariamente{' '}
          <strong>total</strong>: vale para todas as suas especialidades, e por isso o campo
          "Especialidades" fica desabilitado. Para bloquear só uma especialidade, escolha antes a
          empresa. As empresas enxergam o bloqueio na agenda, mas não podem editá-lo nem excluí-lo.
        </InfoAlert>
      )}

      <div className='row'>
        <FormSelect
          formik={formik}
          name='especialidades'
          label='Especialidades'
          placeholder={
            indisponibilidadeGlobal
              ? 'Bloqueio total — não se aplica'
              : disponibilidade
              ? 'Selecione as especialidades'
              : 'Opcional — bloqueia todas se vazio'
          }
          options={opcoesEspecialidades}
          isMulti
          className='col-md-6 mb-5'
          isRequired={disponibilidade}
          isDisabled={indisponibilidadeGlobal}
          infoText={
            indisponibilidadeGlobal
              ? 'Sem instituição, o bloqueio vale para todas as suas especialidades — escolher uma não restringiria nada.'
              : 'Numa indisponibilidade, deixar em branco bloqueia todas as especialidades.'
          }
        />

        <FormSelect
          formik={formik}
          name='tiposAtendimento'
          label='Tipos de atendimento'
          placeholder='Selecione os tipos'
          fetchOptions={OptionsHelper.getOptionsTiposAtendimento}
          isMulti
          className='col-md-6 mb-5'
          isRequired={disponibilidade}
        />
      </div>

      <div className='row'>
        <FormDateInput
          formik={formik}
          name='dataInicio'
          label='Início da vigência'
          className='col-md-3 mb-5'
          isRequired
        />
        <FormDateInput
          formik={formik}
          name='dataTermino'
          label='Fim da vigência'
          className='col-md-3 mb-5'
          minDate={formik.values.dataInicio || undefined}
          infoText='Em branco, a escala vale só no dia de início.'
        />
        <FormTimeInput
          formik={formik}
          name='horarioInicio'
          label='Horário de início'
          className='col-md-3 mb-5'
          isRequired={disponibilidade}
        />
        <FormTimeInput
          formik={formik}
          name='horarioTermino'
          label='Horário de término'
          className='col-md-3 mb-5'
          isRequired={disponibilidade}
        />
      </div>

      <FormSelect
        formik={formik}
        name='diasSemana'
        label='Dias da semana'
        placeholder='Em branco, repete em todos os dias do período'
        options={DIAS_SEMANA_OPTIONS}
        isMulti
        className='mb-5'
        infoText='Define em quais dias a escala se repete dentro do período de vigência.'
      />

      {!disponibilidade && !formik.values.horarioInicio && (
        <InfoAlert variant='info' title='Bloqueio de dia inteiro' showIcon>
          Sem horário de início e término, a indisponibilidade bloqueia o dia inteiro.
        </InfoAlert>
      )}

      <div className='d-flex justify-content-end pt-3'>
        <Button
          className='btn-secondary me-2'
          type={ButtonType.Reset}
          onClick={fecharComConfirmacao}
          disabled={formik.isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          className='btn-primary'
          type={ButtonType.Submit}
          disabled={formik.isSubmitting || carregandoVinculos}
        >
          {formik.isSubmitting ? (
            <LoadingIndicator />
          ) : escalaOriginal ? (
            'Substituir escala'
          ) : (
            'Salvar escala'
          )}
        </Button>
      </div>
    </form>
  )
}
